import { getUSCurrency } from "@/global/utils/getUSCurrency";
import { CartManagerState } from "@/global/store/data/purchaseSeats/cartManager";
import { PerkItemType, PerkParentType } from "./SelectPerks/perks.types";
import { TeamConfig } from "@/global/hooks/useTeamConfig";
import { pluralize } from "@/global/utils/utils";

export function findPerkParentByCode(
  perkParents: PerkParentType[],
  code: string
) {
  return perkParents.find((perkParent) => perkParent.perkCode === code);
}

export function findAllPerksLocations(cartManager?: CartManagerState) {
  if (!cartManager) return [];
  const locations: string[] = [];
  cartManager.cart.forEach((cart) => {
    cart.perks?.forEach((perk) => {
      if (perk.location) locations.push(perk.location);
    });
  });
  return locations;
}

export function isCartHasFailedPerks(cartManager: CartManagerState) {
  return cartManager.cart.some((cart) =>
    cart.perks?.some((perk) => !!perk.errorMessage)
  );
}

export function cartHasFailedMandatoryPerks(cartManager: CartManagerState) {
  return cartManager.cart.some((cart) =>
    cart.perks?.some((perk) => !!perk.errorMessage && perk.isMandatory)
  );
}

export function findMandatoryPerks(perkGroup: PerkParentType[]) {
  return perkGroup
    .map((pg) => pg.perks)
    .flat()
    .filter((p) => p?.isMandatory);
}

export function isAllMandatoryPerksSelected(
  perkGroups: PerkParentType[],
  cartManager: CartManagerState
) {
  const isMandatoryPerksAvailable = perkGroups.some((perkGroup) => {
    const mandatoryPerks = findMandatoryPerks([perkGroup]);
    return mandatoryPerks.length;
  });
  if (!isMandatoryPerksAvailable) return true;

  return perkGroups.every((perkGroup) => {
    const mandatoryPerks = findMandatoryPerks([perkGroup]);
    if (mandatoryPerks.length === 0) return true;
    return mandatoryPerks.every((perk) => {
      return cartManager.cart.some((cartEvent) => {
        return cartEvent.perks?.some((p) => p.perkCode === perk.perkCode);
      });
    });
  });
}

export function getPerkPriceUnitName(
  perkItem: PerkItemType,
  teamConfig?: TeamConfig
) {
  if (perkItem?.perkCode == "parking") return "Parking Pass";
  if (teamConfig?.languages?.perksPage?.perkItemPriceLabel) {
    return teamConfig.languages.perksPage.perkItemPriceLabel;
  }

  return "seat";
}

export function calcPerkItemPrice(
  perkItem: PerkItemType,
  teamConfig: TeamConfig
) {
  const { uiConfigs: { shouldAddServiceFeeWithPerkPrice, perks } } = teamConfig; // prettier-ignore

  // getting the base price
  const perkPrice = shouldAddServiceFeeWithPerkPrice
    ? perkItem?.totalAllInPrice || perkItem.basePrice + perkItem.serviceFee
    : (perkItem.totalBasePrice as number) || perkItem.basePrice || 0;

  const perkSeatCount = perkItem?.seatCount;

  // generating the results
  // -----------------------------------------------
  if (perks?.displayOnlyPrice?.includes(perkItem?.perkCode)) {
    return `${getUSCurrency(perkPrice)}`;
  }

  if (perkPrice == 0 || perkItem.isMandatory) {
    return `${perkSeatCount} ${pluralize(getPerkPriceUnitName(perkItem), perkSeatCount)}`; // prettier-ignore
  }

  if (
    perkItem.perkType == "suite-level" ||
    perkItem.seatCountVariation == "all"
  ) {
    return `${getUSCurrency(perkPrice)} for ${perkItem.displaySeatCount} ${pluralize(getPerkPriceUnitName(perkItem), perkItem.displaySeatCount || 1)}`; // prettier-ignore
  }

  return `${getUSCurrency(perkPrice * perkSeatCount)} for ${perkSeatCount} ${pluralize(getPerkPriceUnitName(perkItem, teamConfig), perkSeatCount)}` // prettier-ignore
}

export function getPerkImageSliderBtnText(
  perkCode: PerkParentType["perkCode"],
  teamConfig: TeamConfig
) {
  if (perkCode == "merchandise") {
    if (teamConfig?.uiConfigs?.perks?.customMerchandiseButtonText) {
      return teamConfig.uiConfigs.perks.customMerchandiseButtonText;
    }
    return "View Merch";
  }
  if (perkCode == "foodUpgrade") return "View F&B";
  if (perkCode == "allInclusiveFnb") return "View F&B";
  if (perkCode == "catering") return "View Menu";
  return "View Images";
}
